/**
 * MGRS Grid utility functions.
 * Ported from react-leaflet-mgrs-graticule/src/CommonUtils.js
 */

import { forward } from 'mgrs';
import { LatLng, PixelPoint } from './types';

// Indices for GZD boundary coordinates
const SW_INDEX = 0;
const NW_INDEX = 1;
const NE_INDEX = 2;

const LONGITUDE_INDEX = 0;
const LATITUDE_INDEX = 1;

const TEN_K_MGRS_REGEX = /([0-9]+[A-Z])([A-Z]{2})([0-9]{2})/;
const GZD_INDEX = 1;

const latBands = 'CDEFGHJKLMNPQRSTUVWX';

/**
 * Calculate the slope between two points.
 */
export function getLineSlope(pointOne: LatLng, pointTwo: LatLng): number {
    if (pointOne === pointTwo) {
        return 0;
    } else if (pointOne.lng === pointTwo.lng) {
        return NaN;
    } else {
        return (pointTwo.lat - pointOne.lat) / (pointTwo.lng - pointOne.lng);
    }
}

/**
 * Get adjusted latitude given a slope and adjusted longitude.
 */
export function getAdjustedLatitude(slope: number, adjustedLongitude: number, unadjustedLatLong: LatLng): number {
    if (!isNaN(slope)) {
        return unadjustedLatLong.lat + slope * (adjustedLongitude - unadjustedLatLong.lng);
    } else {
        return unadjustedLatLong.lat;
    }
}

/**
 * Get adjusted longitude given a slope and adjusted latitude.
 */
export function getAdjustedLongitude(slope: number, adjustedLatitude: number, unadjustedLatLong: LatLng): number {
    if (slope === 0) {
        throw new Error('getAdjustedLongitude: Zero slope received');
    } else if (!isNaN(slope)) {
        return (adjustedLatitude - unadjustedLatLong.lat + slope * unadjustedLatLong.lng) / slope;
    } else {
        return unadjustedLatLong.lng;
    }
}

/**
 * Get the next MGRS GZD character (skipping I and O).
 */
export function getNextMgrsGzdCharacter(char: string): string {
    const result = String.fromCharCode(char.charCodeAt(0) + 1);
    if (result === 'I' || result === 'O') {
        return getNextMgrsGzdCharacter(result);
    }
    return result;
}

/**
 * Connect a line to the GZD boundary in a given direction.
 */
export function connectToGzdBoundary(
    pointOne: LatLng,
    pointTwo: LatLng,
    direction: 'East' | 'West' | 'North' | 'South'
): LatLng {
    const slope = getLineSlope(pointOne, pointTwo);
    const grid = forward([pointOne.lng, pointOne.lat], 1);
    const match = grid.match(TEN_K_MGRS_REGEX);

    if (!match) {
        return { lat: pointOne.lat, lng: pointOne.lng };
    }

    let adjustedLongitude: number;
    let adjustedLatitude: number;

    switch (direction) {
        case 'East': {
            const gzdEastLongitude = getGzd(match[GZD_INDEX])[NE_INDEX][LONGITUDE_INDEX];
            adjustedLatitude = getAdjustedLatitude(slope, gzdEastLongitude, pointTwo);
            adjustedLongitude = gzdEastLongitude;
            return { lat: adjustedLatitude, lng: adjustedLongitude };
        }

        case 'West': {
            const gzdWestLongitude = getGzd(match[GZD_INDEX])[NW_INDEX][LONGITUDE_INDEX];
            adjustedLatitude = getAdjustedLatitude(slope, gzdWestLongitude, pointTwo);
            adjustedLongitude = gzdWestLongitude;
            return { lat: adjustedLatitude, lng: adjustedLongitude };
        }

        case 'North': {
            const gzdNorthLatitude = getGzd(match[GZD_INDEX])[NW_INDEX][LATITUDE_INDEX];
            adjustedLongitude = getAdjustedLongitude(slope, gzdNorthLatitude, pointTwo);

            // Handle special case for 31V/32V boundary
            const WEST_LNG_32V_BOUNDARY = 3;
            if (
                match[GZD_INDEX] === '31V' &&
                adjustedLongitude < WEST_LNG_32V_BOUNDARY &&
                pointTwo.lng > WEST_LNG_32V_BOUNDARY
            ) {
                adjustedLatitude = getAdjustedLatitude(slope, WEST_LNG_32V_BOUNDARY, pointTwo);
                adjustedLongitude = WEST_LNG_32V_BOUNDARY;
            } else {
                adjustedLatitude = gzdNorthLatitude;
            }
            return { lat: adjustedLatitude, lng: adjustedLongitude };
        }

        case 'South': {
            const gzdSouthLatitude = getGzd(match[GZD_INDEX])[SW_INDEX][LATITUDE_INDEX];
            adjustedLongitude = getAdjustedLongitude(slope, gzdSouthLatitude, pointTwo);
            adjustedLatitude = gzdSouthLatitude;
            return { lat: adjustedLatitude, lng: adjustedLongitude };
        }

        default:
            return { lat: pointOne.lat, lng: pointOne.lng };
    }
}

/**
 * Get all visible GZDs given the corner GZDs.
 */
export function getAllVisibleGzds(nwGzd: string, neGzd: string, seGzd: string, swGzd: string): string[] {
    const GZD_REGEX = /([0-9]+)([A-Z])/;
    const LONGITUDE_BAND_INDEX = 1;
    const LATITUDE_BAND_INDEX = 2;

    // Short circuit
    if (nwGzd === seGzd) {
        return [nwGzd];
    }

    const nwMatch = nwGzd.match(GZD_REGEX);
    const neMatch = neGzd.match(GZD_REGEX);
    const swMatch = swGzd.match(GZD_REGEX);

    if (!nwMatch || !neMatch || !swMatch) {
        return [];
    }

    const nwLongitudeBand = parseInt(nwMatch[LONGITUDE_BAND_INDEX]);
    const nwLatitudeBand = nwMatch[LATITUDE_BAND_INDEX];
    const neLongitudeBand = parseInt(neMatch[LONGITUDE_BAND_INDEX]);
    const swLatitudeBand = swMatch[LATITUDE_BAND_INDEX];

    let result: string[] = [];
    const longitudeBands: string[] = [];

    // If the NW GZD is 32V then also include the relevant 31 series GZDs below it
    if (nwGzd === '32V') {
        longitudeBands.push('31');
    }

    // We span at least two vertical bands
    if (nwLongitudeBand !== neLongitudeBand) {
        for (let i = nwLongitudeBand; i <= neLongitudeBand; i++) {
            longitudeBands.push(i.toString());
        }
        if (nwLatitudeBand !== swLatitudeBand) {
            const initialLongitudeBand = [...longitudeBands];
            let currentLatitudeBand = swLatitudeBand;

            while (currentLatitudeBand <= nwLatitudeBand) {
                for (let i = 0; i < initialLongitudeBand.length; i++) {
                    result.push(initialLongitudeBand[i] + currentLatitudeBand);
                }
                currentLatitudeBand = getNextMgrsGzdCharacter(currentLatitudeBand);
            }
        } else {
            // Append the alpha character to the array of GZDs
            for (let i = 0; i < longitudeBands.length; i++) {
                longitudeBands[i] = longitudeBands[i] + nwLatitudeBand;
            }
            result = longitudeBands;
        }
    } else {
        // We span a single vertical band
        let currentLatitudeBand = swLatitudeBand;
        const longitudeBand: string[] = [];

        while (currentLatitudeBand <= nwLatitudeBand) {
            longitudeBand.push(nwLongitudeBand.toString() + currentLatitudeBand);
            currentLatitudeBand = getNextMgrsGzdCharacter(currentLatitudeBand);
        }
        result = longitudeBand;
    }

    // Remove non-existent X series GZDs around Svalbard
    result = result.filter((a) => a !== '32X' && a !== '34X' && a !== '36X');

    // Add 32V if 31W is visible
    if (result.includes('31W') && !result.includes('32V')) {
        result.push('32V');
    }

    // Handle special case for 32V
    if (neGzd === '32V' && seGzd === '32U' && !result.includes('31U')) {
        result.push('31U');
    }

    if (nwGzd === '32V' && neGzd === '32V' && !result.includes('31U')) {
        result.push('31U');
    }

    return result;
}

/**
 * Draw a label on the canvas.
 */
export function drawLabel(
    ctx: CanvasRenderingContext2D,
    labelText: string,
    textColor: string,
    backgroundColor: string,
    labelPosition: PixelPoint
): void {
    const textDimensions = ctx.measureText(labelText);
    const textWidth = textDimensions.width;
    // fontBoundingBoxAscent may not be available in all browsers
    const textHeight = (textDimensions as any).fontBoundingBoxAscent
        ? (textDimensions as any).fontBoundingBoxAscent
        : parseInt(ctx.font, 10) - 2;

    const labelX = labelPosition.x;
    const labelY = labelPosition.y;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(labelX - textWidth / 2 - 1, labelY - textHeight + 1, textWidth + 3, textHeight + 2);
    ctx.fillStyle = textColor;
    ctx.fillText(labelText, labelX - textWidth / 2, labelY);
}

/**
 * Get GZD boundary coordinates.
 * Returns array of [sw, nw, ne, se] corners as [lng, lat] pairs.
 */
export function getGzd(gzd: string): [number, number][] {
    const lngBand = parseInt(gzd, 10);
    const latBand = gzd.replace(lngBand.toString(), '');

    // Validate
    if (lngBand < 1 || lngBand > 60) {
        throw new RangeError('longitudeBand must be between 1 and 60');
    }
    if (latBand.length !== 1) {
        throw new RangeError('Invalid latitudeBand provided, should be one letter');
    }
    if (!latBands.includes(latBand)) {
        throw new RangeError(`Invalid latitudeBand provided, valid bands: ${latBands}`);
    }
    // Handle invalid zones 32X, 34X, 36X around Svalbard
    if (latBand === 'X' && (lngBand === 32 || lngBand === 34 || lngBand === 36)) {
        throw new RangeError('Invalid band');
    }

    // Longitude bands 1..60 are 6° each, covering -180W to 180E
    let longitudeMin = -180 + (lngBand - 1) * 6;
    let longitudeMax = longitudeMin + 6;

    // Latitude bands C..X are 8° each, covering 80°S to 84°N
    // Except band X covering 12°
    const i = latBands.indexOf(latBand);
    const latitudeMin = -80 + i * 8;
    let latitudeMax: number;
    if (latBand !== 'X') {
        latitudeMax = latitudeMin + 8;
    } else {
        latitudeMax = latitudeMin + 12;
    }

    // Special case around Norway: Zone 32V is extended 3° west and 31V is shrunk 3°
    if (lngBand === 31 && latBand === 'V') {
        longitudeMax -= 3;
    } else if (lngBand === 32 && latBand === 'V') {
        longitudeMin -= 3;
    }

    // Special case around Svalbard
    if (lngBand === 31 && latBand === 'X') {
        longitudeMax += 3;
    } else if (lngBand === 33 && latBand === 'X') {
        longitudeMin -= 3;
        longitudeMax += 3;
    } else if (lngBand === 35 && latBand === 'X') {
        longitudeMin -= 3;
        longitudeMax += 3;
    } else if (lngBand === 37 && latBand === 'X') {
        longitudeMin -= 3;
    }

    return [
        [longitudeMin, latitudeMin], // SW
        [longitudeMin, latitudeMax], // NW
        [longitudeMax, latitudeMax], // NE
        [longitudeMax, latitudeMin], // SE
    ];
}
